import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { Message, useToaster } from "rsuite";
import RemindRoundIcon from "@rsuite/icons/RemindRound";
import CheckIcon from "@rsuite/icons/Check";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import {
  SectionRibbon,
  Button,
  FormActions,
  GlassCard,
  Badge,
} from "../../../../ui";
import { OverrideSystem } from "./Modal/OverrideSystem";

export const AcumaticaSettings = ({ system, acumatica, onUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { id } = useParams();
  const toaster = useToaster();
  const [selectedUpdates, setSelectedUpdates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fieldMappings = [
    {
      key: "cus_sys_id",
      label: "Customer System ID",
      systemValue: system?.cus_sys_id,
      acumaticaValue: acumatica?.customeruniqueid,
      fieldName: "customeruniqueid",
    },
    {
      key: "customer",
      label: "Customer",
      systemValue: system?.customer_name,
      acumaticaValue: acumatica?.customercontractcustomername,
      fieldName: "customercontractcustomername",
    },
    {
      key: "site",
      label: "Site",
      systemValue: system?.site_name,
      acumaticaValue: acumatica?.servicecontractcustomername,
      fieldName: "servicecontractcustomername",
    },
    {
      key: "room",
      label: "Room",
      systemValue: system?.room,
      acumaticaValue: acumatica?.room,
      fieldName: "room",
    },
    {
      key: "modality",
      label: "Modality",
      systemValue: system?.modality,
      acumaticaValue: acumatica?.modality,
      fieldName: "modality",
    },
    {
      key: "model",
      label: "Model",
      systemValue: system?.model,
      acumaticaValue: acumatica?.model,
      fieldName: "model",
    },
    {
      key: "serial_number",
      label: "Serial Number",
      systemValue: system?.serial_number,
      acumaticaValue: acumatica?.serialnbr,
      fieldName: "serialnbr",
    },
  ];

  const hasDiscrepancy = (field) => {
    return field.systemValue !== field.acumaticaValue && field.acumaticaValue;
  };

  const toggleFieldUpdate = (fieldKey) => {
    setSelectedUpdates((prev) => {
      if (prev.includes(fieldKey)) {
        return prev.filter((key) => key !== fieldKey);
      } else {
        return [...prev, fieldKey];
      }
    });
  };

  const handleUpdateFields = async () => {
    if (selectedUpdates.length === 0) return;

    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/update/system`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          updateFields: selectedUpdates,
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          Successfully updated {selectedUpdates.length} field
          {selectedUpdates.length > 1 ? "s" : ""} from Acumatica.
        </Message>,
        "topCenter",
      );

      setSelectedUpdates([]);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Failed to update fields:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update fields. Please try again.
        </Message>,
        "topCenter",
      );
    }

    setLoading(false);
  };

  const getDiscrepancyCount = () => {
    return fieldMappings.filter(hasDiscrepancy).length;
  };

  const getSyncStatus = () => {
    if (!acumatica) return { variant: "error", text: "Not Found in Acumatica" };
    const discrepancies = getDiscrepancyCount();
    if (discrepancies === 0)
      return { variant: "success", text: "All Fields Synced" };
    return {
      variant: "warning",
      text: `${discrepancies} Field${discrepancies > 1 ? "s" : ""} Need Update`,
    };
  };

  const syncStatus = getSyncStatus();

  return (
    <AcumaticaContainer>
      <SectionRibbon
        title="Acumatica Integration"
        count={getDiscrepancyCount()}
        countLabel="discrepancies"
        variant="primary"
        rightContent={
          <BadgeContainer>
            <Badge variant={syncStatus.variant}>{syncStatus.text}</Badge>
            <OverrideSystem system={system} />
          </BadgeContainer>
        }
      />

      {!acumatica ? (
        <NotFoundCard>
          <GlassCard>
            <NotFoundContent>
              <NotFoundIcon>⚠️</NotFoundIcon>
              <NotFoundTitle>System Not Found in Acumatica</NotFoundTitle>
              <NotFoundDescription>
                This system does not exist in the Acumatica database. Please
                check the system configuration or create the system in Acumatica
                first.
              </NotFoundDescription>
            </NotFoundContent>
          </GlassCard>
        </NotFoundCard>
      ) : (
        <>
          <FieldComparisonSection>
            <FieldHeader>
              <HeaderColumn>Field</HeaderColumn>
              <HeaderColumn>Current System</HeaderColumn>
              <HeaderColumn>Acumatica Value</HeaderColumn>
              <HeaderColumn>Status</HeaderColumn>
              <HeaderColumn>Update</HeaderColumn>
            </FieldHeader>

            {fieldMappings.map((field) => (
              <FieldRow key={field.key} hasDiscrepancy={hasDiscrepancy(field)}>
                <FieldLabel>{field.label}</FieldLabel>

                <FieldValue>
                  <ValueText>{field.systemValue || "N/A"}</ValueText>
                </FieldValue>

                <FieldValue>
                  <ValueText>{field.acumaticaValue || "N/A"}</ValueText>
                </FieldValue>

                <StatusColumn>
                  {hasDiscrepancy(field) ? (
                    <DiscrepancyIndicator>
                      <RemindRoundIcon color="#f5a623" />
                      <StatusText variant="warning">Mismatch</StatusText>
                    </DiscrepancyIndicator>
                  ) : (
                    <SyncIndicator>
                      <CheckIcon color="#27ae60" />
                      <StatusText variant="success">Synced</StatusText>
                    </SyncIndicator>
                  )}
                </StatusColumn>

                <UpdateColumn>
                  {hasDiscrepancy(field) ? (
                    <UpdateCheckbox
                      type="checkbox"
                      checked={selectedUpdates.includes(field.key)}
                      onChange={() => toggleFieldUpdate(field.key)}
                    />
                  ) : (
                    <span>—</span>
                  )}
                </UpdateColumn>
              </FieldRow>
            ))}
          </FieldComparisonSection>

          {selectedUpdates.length > 0 && (
            <UpdateActionsSection>
              <GlassCard>
                <UpdateActionsContent>
                  <UpdateSummary>
                    <UpdateIcon>🔄</UpdateIcon>
                    <UpdateText>
                      Ready to update <strong>{selectedUpdates.length}</strong>{" "}
                      field
                      {selectedUpdates.length > 1 ? "s" : ""} from Acumatica
                    </UpdateText>
                  </UpdateSummary>

                  <FormActions>
                    <Button
                      variant="subtle"
                      onClick={() => setSelectedUpdates([])}
                      disabled={loading}
                    >
                      Clear Selection
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleUpdateFields}
                      loading={loading}
                    >
                      Update Selected Fields
                    </Button>
                  </FormActions>
                </UpdateActionsContent>
              </GlassCard>
            </UpdateActionsSection>
          )}
        </>
      )}
    </AcumaticaContainer>
  );
};

// Styled Components
const AcumaticaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NotFoundCard = styled.div`
  margin-top: 1rem;
`;

const NotFoundContent = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const NotFoundIcon = styled.div`
  font-size: 3rem;
`;

const NotFoundTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
`;

const NotFoundDescription = styled.p`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.6);
  max-width: 500px;
  line-height: 1.6;
  margin: 0;
`;

const FieldComparisonSection = styled.div`
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FieldHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 1.5fr 1fr 0.8fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.05);
  font-weight: 600;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
`;

const HeaderColumn = styled.div`
  @media (max-width: 768px) {
    &:nth-child(n + 3) {
      grid-column: 1 / -1;
    }
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 1.5fr 1fr 0.8fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: ${({ hasDiscrepancy }) =>
    hasDiscrepancy ? "rgba(245, 166, 35, 0.05)" : "transparent"};
  transition: background-color 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto auto;
    gap: 0.5rem;
  }
`;

const FieldLabel = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
`;

const FieldValue = styled.div`
  display: flex;
  align-items: center;
`;

const ValueText = styled.span`
  font-family: "Monaco", "Menlo", monospace;
  background: rgba(0, 0, 0, 0.05);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.8);
`;

const StatusColumn = styled.div`
  display: flex;
  align-items: center;
`;

const DiscrepancyIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SyncIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusText = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ variant }) => {
    switch (variant) {
      case "warning":
        return "#f5a623";
      case "success":
        return "#27ae60";
      default:
        return "rgba(0, 0, 0, 0.6)";
    }
  }};
`;

const UpdateColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UpdateCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const UpdateActionsSection = styled.div`
  margin-top: 1rem;
`;

const UpdateActionsContent = styled.div`
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const UpdateSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UpdateIcon = styled.div`
  font-size: 1.5rem;
`;

const UpdateText = styled.div`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.8);
`;
